import { useWindowScroll, useDebounceFn, useInfiniteScroll } from '@vueuse/core'

interface InfiniteScrollOptions {
  distance?: number
  debounce?: number
}
/**
 * @function useInfiniteScrollComposable
 * @param functionTriggeredByScroll - A callback function that triggers when the scroll reaches near the bottom.
 * @param options - An optional object to configure `distance` from bottom and `debounce` timing.
 *    - `distance`: The distance (in pixels) from the bottom of the window to trigger the callback. Defaults to 100.
 *    - `debounce`: The debounce time (in milliseconds) to limit the frequency of the scroll handler. Defaults to 300ms.
 * @returns Reactive `x` and `y` scroll positions.
 */
export function useInfiniteScrollComposable(
  functionTriggeredByScroll: () => void,
  options: InfiniteScrollOptions = {},
) {
  const { distance = 100, debounce = 300 } = options // Provide default values

  const { x, y } = useWindowScroll()

  // Debounced scroll handler
  const scrollHandler = useDebounceFn(() => {
    const nearBottom = (window.innerHeight + y.value) >= (document.body.scrollHeight - distance)

    if (nearBottom) {
      try {
        functionTriggeredByScroll() // Trigger the provided function
      }
      catch (error) {
        console.error('Error executing the scroll-triggered function:', error)
      }
    }
  }, debounce)

  // Set up infinite scroll functionality with the distance option
  useInfiniteScroll(window, scrollHandler, { distance })

  return { x, y } // Return reactive scroll positions if needed
}

/**
* Usage Example :
*    // setup infinite scroll
*   useInfiniteScrollComposable(fetchMoreData, {
*       distance: 150,   // Customize the trigger distance
*       debounce: 500,   // Customize debounce timing
*   });
*/
